import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { subDays, format } from 'date-fns';

// GraphQL query to get the count of unique target+template_id combinations with unviewed nuclei scan results
// This counts by how cards are grouped in the UI rather than individual results
const GET_NUCLEI_COUNT = gql`
  query GetNucleiCount($startDate: timestamp!, $endDate: timestamp!) {
    nuclei_scan_results(
      where: {
        viewed_at: {_is_null: true},
        scan_date: {_gte: $startDate, _lte: $endDate}
      },
      distinct_on: [target_id, template_id]
    ) {
      target_id
      template_id
    }
  }
`;

export const NucleiCountContext = createContext(null);

export function NucleiCountProvider({ children }) {
  const [count, setCount] = useState(0);
  
  // Memoize dates
  const startDate = useMemo(() => 
    format(subDays(new Date(), 30), 'yyyy-MM-dd'), 
  []);
  const endDate = useMemo(() => 
    format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 
  []);

  // Updated query config
  const { data, loading, refetch } = useQuery(GET_NUCLEI_COUNT, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 1800000,
    notifyOnNetworkStatusChange: false,
    onError: (error) => console.error('Error fetching nuclei scan count:', error)
  });

  // Remove manual polling logic
  useEffect(() => {
    const newCount = data?.nuclei_scan_results?.length || 0;
    setCount(prev => prev !== newCount ? newCount : prev);
  }, [data]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({ 
    count, 
    setCount, 
    loading, 
    refetch 
  }), [count, loading, refetch]);

  return (
    <NucleiCountContext.Provider value={contextValue}>
      {children}
    </NucleiCountContext.Provider>
  );
}

NucleiCountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useNucleiCount() {
  const context = useContext(NucleiCountContext);
  if (!context) {
    throw new Error('useNucleiCount must be used within a NucleiCountProvider');
  }
  return context;
} 